import { useState } from 'react';
import { Archive, LayoutGrid, Pencil, Plus } from 'lucide-react';
import { formatarData, type QuadroConfigDto } from '@nexital/shared';
import { useDependencias } from '../../application/dependencias';
import { usePedirMotivo } from '../design-system/Confirmacao';
import { CampoConfig, Grupo, ouNulo, useCarga, useGravar } from './campos';

type Edicao = { nome: string; descricao: string };

function Formulario({ inicial, rotulo, salvar, cancelar }: { inicial: Edicao; rotulo: string; salvar: (e: Edicao) => Promise<boolean>; cancelar?: () => void }) {
  const [e, definir] = useState(inicial);
  const { gravando } = useGravar();
  return (
    <div className="config__campos">
      <CampoConfig rotulo="Nome" valor={e.nome} definir={(nome) => definir({ ...e, nome })} />
      <CampoConfig rotulo="Descrição" valor={e.descricao} definir={(descricao) => definir({ ...e, descricao })} largura="longa" />
      <div className="config__grupo-acoes">
        {cancelar && (
          <button type="button" className="botao botao--secundario" onClick={cancelar}>
            Cancelar
          </button>
        )}
        <button type="button" className="botao botao--primario" disabled={gravando || e.nome.trim().length < 2} onClick={() => void salvar(e).then((ok) => ok && definir(inicial))}>
          {rotulo}
        </button>
      </div>
    </div>
  );
}

function Espaco({ q, aoMudar }: { q: QuadroConfigDto; aoMudar: () => void }) {
  const { configuracoes } = useDependencias();
  const pedirMotivo = usePedirMotivo();
  const { gravar } = useGravar();
  const [editando, definirEditando] = useState(false);
  const salvar = async (e: Edicao) => {
    const ok = await gravar(() => configuracoes.alterarQuadro(q.uuid, { nome: e.nome.trim(), descricao: ouNulo(e.descricao) }), 'Espaço atualizado.');
    if (ok) {
      definirEditando(false);
      aoMudar();
    }
    return ok;
  };
  const arquivar = async () => {
    const motivo = await pedirMotivo({ titulo: `Arquivar ${q.nome}?`, mensagem: `O quadro sai da lista da empresa para todos (${q.membros} pessoa(s), ${q.processos} processo(s)).`, rotulo: 'Arquivar' });
    if (motivo && (await gravar(() => configuracoes.arquivarQuadro(q.uuid, motivo), `${q.nome} arquivado.`))) aoMudar();
  };
  return (
    <li className="config__espaco">
      <span className="config__cartao-icone">
        <LayoutGrid size={20} />
      </span>
      {editando ? (
        <Formulario inicial={{ nome: q.nome, descricao: q.descricao ?? '' }} rotulo="Salvar" salvar={salvar} cancelar={() => definirEditando(false)} />
      ) : (
        <div className="config__usuario-dados">
          <strong>{q.nome}</strong>
          {q.descricao && <span>{q.descricao}</span>}
          <small>
            {q.membros} pessoa(s) · {q.processos} processo(s) · criado em {formatarData(q.criadoEm)}
          </small>
        </div>
      )}
      {!editando && (
        <div className="config__acoes-linha">
          <button type="button" className="botao-icone" aria-label={`Editar ${q.nome}`} onClick={() => definirEditando(true)}>
            <Pencil size={16} />
          </button>
          <button type="button" className="botao-icone" aria-label={`Arquivar ${q.nome}`} onClick={() => void arquivar()}>
            <Archive size={16} />
          </button>
        </div>
      )}
    </li>
  );
}

/** Espaços (quadros) da empresa (ADR-0022): criar com os estados padrão, renomear e arquivar com motivo. */
export function SecaoEspacos() {
  const { configuracoes } = useDependencias();
  const { dados, erro, recarregar } = useCarga(configuracoes.quadros);
  const { gravar } = useGravar();
  if (erro) return <p className="cartao-acesso__erro">{erro}</p>;
  if (!dados) return <p>Carregando…</p>;
  const criar = async (e: Edicao) => {
    const ok = await gravar(() => configuracoes.criarQuadro({ nome: e.nome.trim(), descricao: ouNulo(e.descricao) }), `Espaço ${e.nome.trim()} criado. Você entra nele como administrador.`);
    if (ok) await recarregar();
    return ok;
  };
  return (
    <div className="config__formulario">
      <Grupo titulo="Novo espaço">
        <Formulario inicial={{ nome: '', descricao: '' }} rotulo="Criar espaço" salvar={criar} />
      </Grupo>
      <p className="config__nota">
        <Plus size={14} aria-hidden /> O espaço nasce com o fluxo padrão de estados (Novo, Em estudo, Avaliação documental, Proposta, Sessão pública, Aguardando resultado, Em execução).
      </p>
      <ul className="config__usuarios">
        {dados.map((q) => (
          <Espaco key={q.uuid} q={q} aoMudar={() => void recarregar()} />
        ))}
      </ul>
    </div>
  );
}
